import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
  IconButton,
  InputAdornment,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
//import { WhatsAppTemplate } from "@/services/api/whatsApp";

interface TemplateSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  templates: any[];
  loading: boolean;
  onSend: (template: any, parameters: string[]) => void;
}

const TemplateSelectionDialog: React.FC<TemplateSelectionDialogProps> = ({
  open,
  onClose,
  templates,
  loading,
  onSend,
}) => {
  const [search, setSearch] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState<any | null>(null);
  const [parameters, setParameters] = useState<string[]>([]);

  const filteredTemplates = useMemo(() => {
    return templates.filter((t) =>
      t.TemplateName.toLowerCase().includes(search.toLowerCase()) ||
      t.BodyFormat.toLowerCase().includes(search.toLowerCase())
    );
  }, [templates, search]);

  const extractPlaceholders = (text: string) => {
    const matches = text.match(/{{(\d+)}}/g);
    if (!matches) return 0;
    // Get the unique numbers to know how many parameters we need
    const nums = matches.map(m => parseInt(m.match(/\d+/)![0]));
    return Math.max(...nums);
  };

  const handleSelectTemplate = (template: any) => {
    setSelectedTemplate(template);
    const count = extractPlaceholders(template.BodyFormat);
    setParameters(new Array(count).fill(""));
  };

  const handleParamChange = (index: number, value: string) => {
    const newParams = [...parameters];
    newParams[index] = value;
    setParameters(newParams);
  };

  const getPreviewText = (template: any, params: string[]) => {
    let text = template.BodyFormat;
    params.forEach((p, i) => {
      text = text.replace(`{{${i + 1}}}`, p || `{{${i + 1}}}`);
    });
    return text;
  };

  const handleSend = () => {
    if (selectedTemplate) {
      onSend(selectedTemplate, parameters);
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedTemplate(null);
    setParameters([]);
    setSearch("");
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: "16px" }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" fontWeight="600">
          {selectedTemplate ? "Fill Template Details" : "Select WhatsApp Template"}
        </Typography>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <Divider />

      <DialogContent sx={{ p: 0, height: '400px', display: 'flex', flexDirection: 'column' }}>
        {!selectedTemplate ? (
          <>
            <Box sx={{ p: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search templates..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: "10px" }
                }}
              />
            </Box>
            <List sx={{ flex: 1, overflow: 'auto', py: 0 }}>
              {loading ? (
                <Typography sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                  Loading templates...
                </Typography>
              ) : filteredTemplates.length === 0 ? (
                <Typography sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                  No templates found.
                </Typography>
              ) : (
                filteredTemplates.map((template) => (
                  <ListItem 
                    component="button"
                    key={template.TemplateId} 
                    onClick={() => handleSelectTemplate(template)}
                    sx={{ 
                      borderBottom: '1px solid #f0f0f0',
                      '&:hover': { backgroundColor: '#f8fafc' },
                      cursor: 'pointer',
                      textAlign: 'left',
                      width: '100%'
                    }}
                  >
                    <ListItemText 
                      primary={template.TemplateName} 
                      secondary={template.BodyFormat}
                      primaryTypographyProps={{ fontWeight: '600', color: '#1e293b' }}
                      secondaryTypographyProps={{ 
                        sx: { 
                          display: '-webkit-box', 
                          WebkitLineClamp: 2, 
                          WebkitBoxOrient: 'vertical', 
                          overflow: 'hidden' 
                        } 
                      }}
                    />
                  </ListItem>
                ))
              )}
            </List>
          </>
        ) : (
          <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ p: 3, bgcolor: '#f1f5f9', borderRadius: '12px', borderLeft: '4px solid #2563eb' }}>
              <Typography variant="subtitle2" color="primary" fontWeight="700" gutterBottom>
                PREVIEW
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', color: '#334155' }}>
                {getPreviewText(selectedTemplate, parameters)}
              </Typography>
            </Box>

            {parameters.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="subtitle2" fontWeight="600" color="text.secondary">
                  PARAMETERS
                </Typography>
                {parameters.map((param, index) => (
                  <TextField
                    key={`${param}-${index}`}
                    fullWidth
                    label={`Placeholder {{${index + 1}}}`}
                    value={param}
                    onChange={(e) => handleParamChange(index, e.target.value)}
                    size="small"
                  />
                ))}
              </Box>
            )}
            
            <Button 
                onClick={() => setSelectedTemplate(null)} 
                variant="text" 
                size="small"
                sx={{ alignSelf: 'flex-start' }}
            >
                ← Back to list
            </Button>
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        {selectedTemplate && (
          <Button 
            onClick={handleSend} 
            variant="contained" 
            disableElevation
            sx={{ 
                borderRadius: "10px", 
                px: 4,
                bgcolor: '#2563eb',
                '&:hover': { bgcolor: '#1d4ed8' }
            }}
          >
            Send Template
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TemplateSelectionDialog;
